
import { Subject, from, of as observableOf } from 'rxjs'

import { DndContext, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { mdiDownload, mdiPlus } from '@mdi/js'
import Icon from '@mdi/react'
import IconEdit from '@mui/icons-material/Edit'
import IconLock from '@mui/icons-material/Lock'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Tooltip from '@mui/material/Tooltip'
import { ConnectedFileInput } from 'components/FileHelpers'
import { FileFolder } from 'interfaces'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { catchError, switchMap } from 'rxjs/operators'
import { userUnlimitedLimitsSelector } from 'services/product/selectors'
import { message } from 'services/snackbar'
import { reorderFolders, setEditFolder, setUploadsDialogOpen } from 'services/uploads/actions'
import { myFileFoldersArraySelector, myUploadsStorageSelector } from 'services/uploads/selectors'
import { libraryFilesToCSV } from 'services/uploads/util'
import { bytesToSize, LIMIT_MY_LIBRARY_STORAGE } from 'shared'
import { downloadCSV } from 'shared/utils/file/csv'
import { StoreThunkDispatch } from 'store/state'
import { reorder, sortByKeyAscending } from 'utils/sort/order'
import styles from '../AppNavigation.pcss'
import { AppNavLink } from '../AppNavLink'


interface LibraryNavMenuProps {
  expanded: boolean
  onToggleExpanded: () => void
}

export function LibraryNavMenu(props: LibraryNavMenuProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const fileFolders = useSelector(myFileFoldersArraySelector)
  const storage = useSelector(myUploadsStorageSelector)
  const isStorageUnlimited = Boolean(useSelector(userUnlimitedLimitsSelector)[LIMIT_MY_LIBRARY_STORAGE])
  const foldersMap = useMemo(() => fileFolders.reduce((map: Record<string, FileFolder>, f: FileFolder) => {
    map[f.id] = f
    return map
  }, {}), [fileFolders])
  const idOrder = useMemo(() => fileFolders.slice().sort(sortByKeyAscending('order')).map(f => f.id), [fileFolders])
  const [order, setOrder] = useState<string[]>(idOrder)
  const { setNodeRef: setDroppableNodeRef } = useDroppable({ id: 'droppable' })
  const orderRef = useRef<string[]>(idOrder)
  const reorder$ = useRef<Subject<string[]>>(undefined)

  useEffect(() => {
    setOrder(idOrder)
    orderRef.current = idOrder
  }, [idOrder])

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.pipe(switchMap((ids) => {
      return from(dispatch(reorderFolders(ids)).unwrap()).pipe(catchError((error) => {
        dispatch(message(intl.formatMessage({ id: 'notifications.generic.update-failed' }), 'error'))
        return observableOf({ error })
      }))
    }))
      .subscribe((response: any) => {
        if (response.error) {
          // revert to previous ordering
          setOrder(orderRef.current)
        } else {
          setOrder(response.ids)
          orderRef.current = response.ids
        }
      })
  }, [dispatch, intl])

  const editFileFolder = (f: FileFolder) => {
    dispatch(setEditFolder(f))
  }

  const openFileUpload = () => {
    dispatch(setUploadsDialogOpen(true))
  }

  const exportAllLinks = () => {
    const entries = fileFolders.flatMap((folder: FileFolder) =>
      Object.values(folder.files || {}).map(file => ({ folderName: folder.name, file }))
    )
    downloadCSV(libraryFilesToCSV(entries), 'library-links.csv')
  }

  const onDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over.id) {
      const oldIndex = order.indexOf(active.id)
      const newIndex = order.indexOf(over.id)
      const reordered = reorder(order, oldIndex, newIndex)
      setOrder(reordered)
      reorder$.current?.next(reordered)
    }
  }

  const hasFiles = fileFolders.some((folder: FileFolder) => Object.keys(folder.files || {}).length > 0)

  // EXPL: Do not show value less than 5% as it will not be visible in the progress bar
  // eslint-disable-next-line no-magic-numbers
  const storageProgress = isStorageUnlimited ? 0 : Math.max(5, storage.used * 100 / storage.limit)
  const storageLabel = isStorageUnlimited
    ? `${bytesToSize(storage.used)} used`
    : `${bytesToSize(storage.used)} of ${bytesToSize(storage.limit)} used`

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  )

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={order} strategy={rectSortingStrategy} modifiers={[restrictToParentElement]}>
        <div ref={setDroppableNodeRef} data-testid="library-nav">
          <AppNavLink
            expanded={props.expanded}
            toggleExpandedOnClick
            label={<FormattedMessage id="app.nav.my-library" />}
            to="/content/library"
            items={
              <Fragment>
                {order.map(id => {
                  const folder = foldersMap[id]
                  if (!folder) {
                    return null
                  }
                  return (
                    <DraggableFolderLink
                      key={folder.id}
                      folder={folder}
                      onEdit={editFileFolder}
                    />
                  )
                })}
                <ConnectedFileInput inputId="library-file-select" onChange={openFileUpload} className={styles['btn-upload']}>
                  <div className={`${styles['child-link']} ${styles['link-action']} ${styles['btn-gap4']}`}>
                    <Icon path={mdiPlus} size="18px" />
                    <FormattedMessage id="app.nav.my-library-create" />
                  </div>
                </ConnectedFileInput>
                {hasFiles && (
                  <div
                    role="button"
                    data-testid="btn-export-links"
                    className={`${styles['child-link']} ${styles['link-action']} ${styles['btn-gap4']}`}
                    onClick={exportAllLinks}
                  >
                    <Icon path={mdiDownload} size="18px" />
                    <FormattedMessage id="app.nav.my-library-export" />
                  </div>
                )}
                {(isStorageUnlimited || storage.limit > 0) && (
                  <div className={styles.progress}>
                    <LinearProgress
                      variant="determinate"
                      value={storageProgress}
                      classes={{ root: styles.line, bar: styles.bar }}
                    />
                    <div className={styles['progress-label']}>{storageLabel}</div>
                  </div>
                )}
              </Fragment>
            }
            onToggleExpanded={props.onToggleExpanded}
          />
        </div>
      </SortableContext>
    </DndContext>
  )
}

interface DraggableFolderLinkProps {
  folder: FileFolder
  onEdit: (f: FileFolder) => void
}
function DraggableFolderLink(props: DraggableFolderLinkProps) {
  const { folder } = props
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: folder.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    pointerEvents: isDragging ? 'none' : 'all'
  } as any

  const edit = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    props.onEdit(folder)
  }

  return (
    <div className={styles['folders-nav']} ref={setNodeRef} style={style} data-testid="file-folder-link">
      <AppNavLink
        id={folder.id}
        to={`/content/library/${folder.id}`}
        small
        label={(
          <div className={`${styles['name-box']}`} {...listeners} {...attributes}>
            {folder.isPrivate && <IconLock className={styles['stream-icon-lock']} />}
            <span className={`${styles.name} text-ellipsis`} data-testid="file-folder-name">{folder.name}</span>
            <span className={styles.count}>{`(${folder.filesCount})`}</span>
          </div>
        )}
        actions={(
          <Tooltip title={<FormattedMessage id="actions.edit" />} placement="top">
            <IconButton size="small" data-testid="btn-edit" onClick={edit}>
              <IconEdit className={styles['link-icon']} />
            </IconButton>
          </Tooltip>
        )}
        className={styles['child-link']}
      />
    </div>
  )
}
